import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Chip,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import buildRoute from 'components/buildRoute';
import PageHeader from 'components/PageHeader';
import { getCsrfToken } from 'utils/csrf';
import { deleteRecord } from '../../hartUtils';
import { trackEvent } from '../../../../configureMixpanel';

const DataRestrictionFormPage = () => {
  const navigate = useNavigate();
  const { projectId, firewallId } = useParams();
  const isEditing = !!firewallId;

  const [formData, setFormData] = useState({
    name: '',
    appliesTo: 'Job',
    expiration_date: '',
    allow_list: [],
    deny_list: [],
    selected_jobs: [],
    selected_clients: [],
  });
  
  const [clientMap, setClientMap] = useState({}); // Store client ID -> name mapping
  const [jobMap, setJobMap] = useState({}); // Store job_no -> job_name mapping

  const [newAllowEmail, setNewAllowEmail] = useState([]);
  const [newDenyEmail, setNewDenyEmail] = useState([]);
  const [newJob, setNewJob] = useState([]);
  const [newClient, setNewClient] = useState([]);
  const [errors, setErrors] = useState({});
  const [allowListValidationError, setAllowListValidationError] = useState('');
  const [denyListValidationError, setDenyListValidationError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingAnalysts, setLoadingAnalysts] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (isEditing) {
        // Fetch restriction and dropdowns in parallel so detail data appears as soon as it's ready
        await Promise.all([
          fetchRestriction(),
          fetchJobs(),
          fetchClients(),
          fetchAnalysts(),
        ]);
      } else {
        await Promise.all([
          fetchJobs(),
          fetchClients(),
          fetchAnalysts(),
        ]);
      }
    };

    loadData();
  }, [isEditing, firewallId]);

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const response = await fetch('/hart/jobs?dropdown=true', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch jobs (${response.status})`);
      }
      
      const data = await response.json();
      // With dropdown=true, backend returns an array directly (no pagination)
      const jobsArray = Array.isArray(data) ? data : (data.results || []);
      setJobs(jobsArray);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await fetch('/hart/clients?dropdown=true', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch clients (${response.status})`);
      }
      
      const data = await response.json();
      // With dropdown=true, backend returns an array directly (no pagination)
      const clientsArray = Array.isArray(data) ? data : (data.results || []);
      setClients(clientsArray);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchAnalysts = async () => {
    setLoadingAnalysts(true);
    try {
      const response = await fetch('/hart/analysts?dropdown=true&show_all_jobs=true', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch analysts (${response.status})`);
      }

      const data = await response.json();
      // Handle both array and paginated response formats
      const analystsArray = Array.isArray(data) ? data : (data.results || []);
      setAnalysts(analystsArray);
    } catch (error) {
      console.error('Error fetching analysts:', error);
      setAnalysts([]);
    } finally {
      setLoadingAnalysts(false);
    }
  };

  const fetchRestriction = async () => {
    try {
      const response = await fetch(`/hart/data_restrictions/${firewallId}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch restriction (${response.status})`);
      }
      
      const data = await response.json();
      const appliesTo = data.restriction_type === 'client' ? 'Client' : 'Job';
      
      // API returns client objects with {id, name}, store mapping and IDs
      let clientIds = [];
      let clientNameMap = {};
      if (data.clients && data.clients.length > 0) {
        data.clients.forEach(client => {
          if (typeof client === 'object') {
            clientIds.push(client.id);
            clientNameMap[client.id] = client.name;
          } else {
            clientIds.push(client);
          }
        });
      }
      
      // API returns job objects with {job_no, job_name}, store mapping and job_no
      let jobNumbers = [];
      let jobNameMap = {};
      if (data.jobs && data.jobs.length > 0) {
        data.jobs.forEach(job => {
          if (typeof job === 'object') {
            jobNumbers.push(job.job_no);
            jobNameMap[job.job_no] = job.job_name;
          } else {
            jobNumbers.push(job);
          }
        });
      }
      
      setClientMap(clientNameMap);
      setJobMap(jobNameMap);
      setFormData({
        name: data.name || '',
        appliesTo: appliesTo,
        expiration_date: data.expiration_date ? data.expiration_date.split('T')[0] : '',
        allow_list: data.allow_list || [],
        deny_list: data.deny_list || [],
        selected_jobs: jobNumbers,
        selected_clients: clientIds,
      });
    } catch (error) {
      console.error('Error fetching data restriction:', error);
      alert('Failed to load data restriction. Please try again.');
    }
  };

  const handleAddJob = () => {
    if (newJob && newJob.length > 0) {
      const jobsToAdd = newJob
        .map(job => job.job_no)
        .filter(jobNo => !formData.selected_jobs.includes(jobNo));
      
      setFormData({
        ...formData,
        selected_jobs: [...formData.selected_jobs, ...jobsToAdd],
      });
      setNewJob([]);
    }
  };

  const handleRemoveJob = (jobNo) => {
    setFormData({
      ...formData,
      selected_jobs: formData.selected_jobs.filter(j => j !== jobNo),
    });
  };

  const handleAddClient = () => {
    if (newClient && newClient.length > 0) {
      const clientsToAdd = newClient
        .map(client => client.id)
        .filter(clientId => !formData.selected_clients.includes(clientId));
      
      setFormData({
        ...formData,
        selected_clients: [...formData.selected_clients, ...clientsToAdd],
      });
      setNewClient([]);
    }
  };

  const handleRemoveClient = (clientId) => {
    setFormData({
      ...formData,
      selected_clients: formData.selected_clients.filter(c => c !== clientId),
    });
  };

  const handleAddAllowEmail = () => {
    if (!newAllowEmail?.length) return;

    const validEmails = [];
    const invalidEmails = [];

    newAllowEmail.forEach(item => {
      const email = typeof item === 'string' ? item : item.email;
      if (!email) return;

      const isValidAnalyst = analysts.some(a => a.email === email);
      const alreadyAdded = formData.allow_list.includes(email);

      if (isValidAnalyst && !alreadyAdded) {
        validEmails.push(email);
      } else if (!isValidAnalyst) {
        invalidEmails.push(email);
      }
    });

    if (validEmails.length > 0) {
      setFormData({ ...formData, allow_list: [...formData.allow_list, ...validEmails] });
    }

    setAllowListValidationError(
      invalidEmails.length > 0
        ? `The following emails do not match any analyst: ${invalidEmails.join(', ')}`
        : ''
    );
    setNewAllowEmail([]);
  };

  const handleAllowEmailPaste = (event) => {
    const pastedText = event.clipboardData.getData('text');

    if (!pastedText.includes(',')) return;

    event.preventDefault();

    const emailStrings = pastedText.split(',').map(email => email.trim()).filter(Boolean);
    const validEmails = [];
    const invalidEmails = [];

    emailStrings.forEach(emailString => {
      const analyst = analysts.find(a => a.email === emailString);
      const alreadyAdded = formData.allow_list.includes(emailString);

      if (analyst && !alreadyAdded) {
        validEmails.push(analyst.email);
      } else if (!analyst) {
        invalidEmails.push(emailString);
      }
    });

    if (validEmails.length > 0) {
      setFormData({ ...formData, allow_list: [...formData.allow_list, ...validEmails] });
    }

    setAllowListValidationError(
      invalidEmails.length > 0
        ? `The following emails do not match any analyst: ${invalidEmails.join(', ')}`
        : ''
    );
  };

  const handleRemoveAllowEmail = (email) => {
    setFormData({
      ...formData,
      allow_list: formData.allow_list.filter(e => e !== email),
    });
  };

  const handleClearAllowList = () => {
    setFormData({ ...formData, allow_list: [] });
    setAllowListValidationError('');
  };

  const handleAddDenyEmail = () => {
    if (!newDenyEmail?.length) return;

    const validEmails = [];
    const invalidEmails = [];

    newDenyEmail.forEach(item => {
      const email = typeof item === 'string' ? item : item.email;
      if (!email) return;

      const isValidAnalyst = analysts.some(a => a.email === email);
      const alreadyAdded = formData.deny_list.includes(email);

      if (isValidAnalyst && !alreadyAdded) {
        validEmails.push(email);
      } else if (!isValidAnalyst) {
        invalidEmails.push(email);
      }
    });

    if (validEmails.length > 0) {
      setFormData({ ...formData, deny_list: [...formData.deny_list, ...validEmails] });
    }

    setDenyListValidationError(
      invalidEmails.length > 0
        ? `The following emails do not match any analyst: ${invalidEmails.join(', ')}`
        : ''
    );
    setNewDenyEmail([]);
  };

  const handleDenyEmailPaste = (event) => {
    const pastedText = event.clipboardData.getData('text');

    if (!pastedText.includes(',')) return;

    event.preventDefault();

    const emailStrings = pastedText.split(',').map(email => email.trim()).filter(Boolean);
    const validEmails = [];
    const invalidEmails = [];

    emailStrings.forEach(emailString => {
      const analyst = analysts.find(a => a.email === emailString);
      const alreadyAdded = formData.deny_list.includes(emailString);

      if (analyst && !alreadyAdded) {
        validEmails.push(analyst.email);
      } else if (!analyst) {
        invalidEmails.push(emailString);
      }
    });

    if (validEmails.length > 0) {
      setFormData({ ...formData, deny_list: [...formData.deny_list, ...validEmails] });
    }

    setDenyListValidationError(
      invalidEmails.length > 0
        ? `The following emails do not match any analyst: ${invalidEmails.join(', ')}`
        : ''
    );
  };

  const handleRemoveDenyEmail = (email) => {
    setFormData({
      ...formData,
      deny_list: formData.deny_list.filter(e => e !== email),
    });
  };

  const handleClearDenyList = () => {
    setFormData({ ...formData, deny_list: [] });
    setDenyListValidationError('');
  };

  const handleSave = async () => {
    // Validate
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    
    // Check if at least one job or client is selected
    const hasJobs = formData.selected_jobs && formData.selected_jobs.length > 0;
    const hasClients = formData.selected_clients && formData.selected_clients.length > 0;
    if (!hasJobs && !hasClients) {
      newErrors.items = 'You must select at least one job or client';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const dataToSave = {
        name: formData.name,
        restriction_type: formData.appliesTo === 'Client' ? 'client' : 'job',
        // Convert date string to ISO datetime format if provided, otherwise null
        expiration_date: formData.expiration_date ? `${formData.expiration_date}T00:00:00Z` : null,
        // Send null instead of empty arrays if no emails
        allow_list: formData.allow_list && formData.allow_list.length > 0 ? formData.allow_list : null,
        deny_list: formData.deny_list && formData.deny_list.length > 0 ? formData.deny_list : null,
        // Add jobs and clients arrays based on restriction type
        jobs: formData.appliesTo === 'Job' && hasJobs ? formData.selected_jobs : [],
        clients: formData.appliesTo === 'Client' && hasClients ? formData.selected_clients : [],
      };

      const response = await fetch(
        isEditing ? `/hart/data_restrictions/${firewallId}` : '/hart/data_restrictions',
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify(dataToSave),
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Save error:', errorData);
        throw new Error(`Failed to save restriction (${response.status})`);
      }

      trackEvent("hart_data_restriction_saved", {
        mode: isEditing ? "edit" : "create",
        success: true,
      });
      navigate(`/projects/${projectId}/hart/admin?tab=2`);
    } catch (error) {
      console.error('Error saving data restriction:', error);
      trackEvent("hart_data_restriction_saved", {
        mode: isEditing ? "edit" : "create",
        success: false,
      });
      alert('Failed to save data restriction. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate(`/projects/${projectId}/hart/admin?tab=2`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this data restriction?')) {
      return;
    }
    try {
      await deleteRecord('/hart/data_restrictions', firewallId);
      trackEvent("hart_data_restriction_deleted", {});
      navigate(`/projects/${projectId}/hart/admin?tab=2`);
    } catch (error) {
      console.error('Error deleting data restriction:', error);
      alert('Failed to delete data restriction. Please try again.');
    }
  };

  return (
    <>
      <PageHeader
        title="Data Restriction"
        subtitle="Control data access with allow and deny lists"
      />

      <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 3 }}>
        {/* Basic Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AssignmentIcon sx={{ fontSize: 20, color: 'warning.main' }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Basic Information
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Data Restriction Name"
                placeholder="Data restriction name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
                size="small"
                InputLabelProps={{ 
                  shrink: true,
                  sx: { color: 'primary.main', fontWeight: 600 }
                }}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Apply to:
                </Typography>
                <RadioGroup
                  row
                  value={formData.appliesTo}
                  onChange={(e) => {
                    setFormData({ ...formData, appliesTo: e.target.value });
                  }}
                >
                  <FormControlLabel value="Job" control={<Radio />} label="Jobs" />
                  <FormControlLabel value="Client" control={<Radio />} label="Clients" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                <Autocomplete
                  multiple
                  options={formData.appliesTo === 'Job' ? jobs : clients}
                  getOptionLabel={(option) => {
                    if (formData.appliesTo === 'Job') {
                      return `Job ${option.job_no} - ${option.job_name || 'Unnamed'}`;
                    } else {
                      return option.name || `Client ${option.id}`;
                    }
                  }}
                  value={formData.appliesTo === 'Job' ? newJob : newClient}
                  onChange={(event, newValue) => {
                    if (formData.appliesTo === 'Job') {
                      setNewJob(newValue);
                    } else {
                      setNewClient(newValue);
                    }
                    // Clear error when selection is made
                    if (newValue && newValue.length > 0) {
                      setErrors((prev) => {
                        const { items: _, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  loading={formData.appliesTo === 'Job' ? loadingJobs : loadingClients}
                  fullWidth
                  forcePopupIcon={true}
                  popupIcon={<ArrowDropDownIcon />}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, color, ...chipProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={
                            formData.appliesTo === 'Job'
                              ? `Job ${option.job_no} - ${option.job_name || 'Unnamed'}`
                              : option.name
                          }
                          {...chipProps}
                          size="small"
                          sx={{
                            bgcolor: '#e0e0e0 !important',
                            color: 'rgba(0, 0, 0, 0.87) !important',
                            '& .MuiChip-deleteIcon': {
                              color: 'rgba(0, 0, 0, 0.6) !important',
                            },
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Select ${formData.appliesTo}s`}
                      placeholder={`Select ${formData.appliesTo.toLowerCase()}s`}
                      size="small"
                      error={!!errors.items}
                      helperText={errors.items}
                      InputLabelProps={{ 
                        shrink: true,
                        sx: { color: 'primary.main', fontWeight: 600 }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {(formData.appliesTo === 'Job' ? loadingJobs : loadingClients) ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Button
                  variant="contained"
                  onClick={formData.appliesTo === 'Job' ? handleAddJob : handleAddClient}
                  disabled={
                    formData.appliesTo === 'Job' 
                      ? !newJob || newJob.length === 0 
                      : !newClient || newClient.length === 0
                  }
                  sx={{ minWidth: 100, py: '6px' }}
                >
                  + Add
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 50 }}>
                {formData.appliesTo === 'Job' ? (
                  formData.selected_jobs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No jobs added
                    </Typography>
                  ) : (
                    formData.selected_jobs.map((jobNo) => {
                      // First check stored map from API, then fall back to jobs dropdown
                      let jobName = jobMap[jobNo];
                      if (!jobName) {
                        const job = jobs.find(j => j.job_no === jobNo);
                        jobName = job?.job_name;
                      }
                      return (
                        <Chip
                          key={jobNo}
                          label={jobName ? `Job ${jobNo} - ${jobName}` : `Job ${jobNo}`}
                          onDelete={() => handleRemoveJob(jobNo)}
                          size="small"
                        />
                      );
                    })
                  )
                ) : (
                  formData.selected_clients.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No clients added
                    </Typography>
                  ) : (
                    formData.selected_clients.map((clientId) => {
                      // First check stored map from API, then fall back to clients dropdown
                      let clientName = clientMap[clientId];
                      if (!clientName) {
                        const client = clients.find(c => String(c.id) === String(clientId));
                        clientName = client?.name;
                      }
                      return (
                        <Chip
                          key={clientId}
                          label={clientName || clientId}
                          onDelete={() => handleRemoveClient(clientId)}
                          size="small"
                        />
                      );
                    })
                  )
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Expiration Date (optional)"
                placeholder="MM/DD/YYYY"
                type="date"
                value={formData.expiration_date}
                onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                size="small"
                InputLabelProps={{ 
                  shrink: true,
                  sx: { color: 'primary.main', fontWeight: 600 }
                }}
                fullWidth
                helperText="When this data access restriction expire (stays active)"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Allow List */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Allow List (Allowed Emails)
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            If allow list has entries, ONLY these users can access.
          </Alert>

          {allowListValidationError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAllowListValidationError('')}>
              {allowListValidationError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={loadingAnalysts}
              options={analysts.filter(analyst => {
                const selectedEmails = newAllowEmail.map(item => typeof item === 'string' ? item : item.email);
                return !formData.allow_list.includes(analyst.email) && !selectedEmails.includes(analyst.email);
              })}
              getOptionLabel={(option) => option.email || ''}
              freeSolo
              value={newAllowEmail}
              onChange={(event, newValue) => {
                setNewAllowEmail(newValue);
                setAllowListValidationError('');
              }}
              loading={loadingAnalysts}
              forcePopupIcon={true}
              popupIcon={<ArrowDropDownIcon />}
              fullWidth
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, color, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={typeof option === 'string' ? option : option.email}
                      {...chipProps}
                      size="small"
                      sx={{
                        bgcolor: '#e0e0e0 !important',
                        color: 'rgba(0, 0, 0, 0.87) !important',
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(0, 0, 0, 0.6) !important',
                        },
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Email Address"
                  placeholder="Select, type, or paste comma-separated email addresses"
                  size="small"
                  onPaste={handleAllowEmailPaste}
                  InputLabelProps={{
                    shrink: true,
                    sx: { color: 'primary.main', fontWeight: 600 }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingAnalysts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="contained"
              onClick={handleAddAllowEmail}
              disabled={!newAllowEmail || newAllowEmail.length === 0}
              sx={{ minWidth: 100, py: '6px' }}
            >
              + Add
            </Button>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 50 }}>
              {formData.allow_list.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No emails added (unrestricted access to anyone not denied)
                </Typography>
              ) : (
                formData.allow_list.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => handleRemoveAllowEmail(email)}
                    size="small"
                  />
                ))
              )}
            </Box>
            {formData.allow_list.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearAllowList}
                >
                  Clear All
                </Button>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Deny List */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CancelIcon sx={{ fontSize: 20, color: 'error.main' }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Deny List (Blocked Emails)
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Deny list overrides allow list. These users are blocked regardless.
          </Alert>

          {denyListValidationError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDenyListValidationError('')}>
              {denyListValidationError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={loadingAnalysts}
              options={analysts.filter(analyst => {
                const selectedEmails = newDenyEmail.map(item => typeof item === 'string' ? item : item.email);
                return !formData.deny_list.includes(analyst.email) && !selectedEmails.includes(analyst.email);
              })}
              getOptionLabel={(option) => option.email || ''}
              freeSolo
              value={newDenyEmail}
              onChange={(event, newValue) => {
                setNewDenyEmail(newValue);
                setDenyListValidationError('');
              }}
              loading={loadingAnalysts}
              forcePopupIcon={true}
              popupIcon={<ArrowDropDownIcon />}
              fullWidth
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, color, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={typeof option === 'string' ? option : option.email}
                      {...chipProps}
                      size="small"
                      sx={{
                        bgcolor: '#e0e0e0 !important',
                        color: 'rgba(0, 0, 0, 0.87) !important',
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(0, 0, 0, 0.6) !important',
                        },
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Email Address"
                  placeholder="Select, type, or paste comma-separated email addresses"
                  size="small"
                  onPaste={handleDenyEmailPaste}
                  InputLabelProps={{
                    shrink: true,
                    sx: { color: 'primary.main', fontWeight: 600 }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingAnalysts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="contained"
              onClick={handleAddDenyEmail}
              disabled={!newDenyEmail || newDenyEmail.length === 0}
              sx={{ minWidth: 100, py: '6px' }}
            >
              + Add
            </Button>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 50 }}>
              {formData.deny_list.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No emails added (no one specifically blocked)
                </Typography>
              ) : (
                formData.deny_list.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => handleRemoveDenyEmail(email)}
                    size="small"
                  />
                ))
              )}
            </Box>
            {formData.deny_list.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearDenyList}
                >
                  Clear All
                </Button>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 3 }}>
          {isEditing && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDelete}
              sx={{ mr: 'auto' }}
            >
              Delete
            </Button>
          )}
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save Changes
          </Button>
        </Box>
      </Box>
    </>
  );
};

const routeArgs = {
  element: <DataRestrictionFormPage />,
};

export default buildRoute(routeArgs);
